<template>
  <portal to="popup">
    <div
      :class="$style.overlay"
      @click="handleOverlayClick"
    >
      <div :class="$style['chart-link-popup']">
        <div :class="$style['chart-link-popup__container']">
          <div :class="$style['chart-link-popup__head']">
            <span :class="$style['chart-link-popup__head-title']">{{ locales('settings_link_settings') }}</span>
            <vgp-icon-button
              :icon="{ name: 'close-1', type: 'bold', size: 24 }"
              @onClick="close"
            />
          </div>
          <div :class="$style['chart-link-popup__body']">
            <div :class="$style['spin-input-container']">
              <label
                for="chart-link-popup-spin-input"
                :class="$style['spin-input-label']"
              >
                <span>{{ locales('settings_lag') }}</span>
                <span>(<strong>{{ locales(`settings_${instance.config.duration_view}`) }}</strong>)</span>
              </label>
              <div :class="$style['spin-input']">
                <button
                  :class="$style['spin-input__btn']"
                  @click="changeValue('decrement')"
                >
                  -
                </button>
                <input
                  id="chart-link-popup-spin-input"
                  ref="linkLagInput"
                  v-model="lag.text"
                  :class="$style['spin-input__field']"
                  @change="handleLagChange"
                >
                <button
                  :class="$style['spin-input__btn']"
                  @click="changeValue('increment')"
                >
                  +
                </button>
              </div>
            </div>
          </div>
          <div :class="$style['chart-link-popup__footer']">
            <vgp-button
              type="destructive"
              :label="locales('settings_delete_link')"
              small
              @onClick="deleteLink"
            />
            <vgp-button
              type="primary"
              :label="locales('common_save')"
              small
              @onClick="saveChanges"
            />
          </div>
        </div>
      </div>
    </div>
  </portal>
</template>

<script>
import _ from 'lodash';
import VgpIconButton from '$$vueCmp/globalButton/withIcon/iconButton.vue';
import dateFormats from '$$helpers/dateFormats';
import timeIntervals from '$$helpers/timeIntervals';
import timeParser from '$$helpers/timeParser';
import VgpButton from '$$vueCmp/globalButton/vgpButton.vue';
import app from '$$app';

export default {
  name: 'ChartLinkPopup',
  components: {
    VgpIconButton,
    VgpButton,
  },
  props: {
    instance: {
      type: Object,
      required: true,
    },
    initialLag: {
      type: Number,
      required: true,
    },
    linkId: {
      type: Number,
      required: true,
    },
    ganttId: {
      type: Number,
      required: true,
    },
    onClose: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      locales: __,
      lag: this.parseInitialLag(),
      appListeners: [],
    };
  },
  computed: {
    linkData() {
      return this.instance.ganttDataApi.getLinkByGanttId(this.ganttId, this.linkId);
    },
  },
  watch: {
    linkData(value) {
      if (_.isEmpty(value)) {
        this.close();
      }
    },
  },
  created() {
    app.trigger('opened:popup:settingsLink', +this.linkId, +this.ganttId);
    app.trigger('popup:show');
    this.appListeners.push(app.on('popup:show', this.close));
  },
  mounted() {
    this.$nextTick(() => {
      this.$refs.linkLagInput.select();
    });
  },
  beforeUnmount() {
    this.appListeners.forEach(id => app.off(id));
    this.appListeners = [];
  },
  methods: {
    deleteLink() {
      this.instance.isLinkExists(this.linkId) && this.instance.deleteLink(this.linkId);
      this.close();
    },
    saveChanges() {
      const link = this.instance.getLink(this.linkId);
      // const startDate = Links.getStartAccordingToType(link);
      // const calcDate = this.instance.calculateToDuration(startDate, this.lag.value, this.instance.config.duration_unit);
      // const trueLag = Links.customCalculateDuration(startDate, calcDate.endDate);

      if (link && link.lag !== this.lag.value) {
        // link.lag = trueLag;
        link.lag = this.lag.value;
        this.instance.updateLink(this.linkId, link);
        userExtAnalytics.log('gantt_link_add_lag_done', { from: 'chart', value: link.lag });
      }

      this.close();
    },
    changeValue(action) {
      const shiftValue = this.getShiftValueInMinutes(action);
      const newValue = this.lag.value + shiftValue;

      this.lag.value = newValue;
      this.lag.text = timeParser.output(newValue, {
        durationData: this.instance.config.durationData,
        durationStep: this.instance.config.duration_view,
        prop: 'lag',
      });
    },
    getShiftValueInMinutes(action) {
      const configDurationStep = this.instance.config.duration_step;
      const configDurationView = this.instance.config.duration_view;
      const customWorkingDays = this.instance.config.durationData.showDay;
      const customWorkingHours = this.instance.config.durationData.showTime;
      let shiftStepValue;
      let workHours = 0;

      const defHours = dateFormats.getHoursArrayByUserTimeFormat();
      const selectedHoursByIntervals = timeIntervals.getHoursArrayByIntervalArray(customWorkingHours, defHours);

      workHours = selectedHoursByIntervals.length / 2;

      switch (configDurationView) {
      case 'day':
        shiftStepValue = workHours * configDurationStep;
        break;
      case 'week':
        shiftStepValue = workHours * customWorkingDays.length * configDurationStep;
        break;
      case 'month':
        shiftStepValue = workHours * 4 * customWorkingDays.length * configDurationStep;
        break;
      default:
        shiftStepValue = configDurationStep;
        break;
      }

      if (action === 'decrement') {
        shiftStepValue *= -1;
      }

      return shiftStepValue * 60;
    },
    parseInitialLag() {
      return {
        value: this.initialLag,
        text: timeParser.output(this.initialLag, {
          durationData: this.instance.config.durationData,
          durationStep: this.instance.config.duration_view,
          prop: 'lag',
        }),
      };
    },
    handleLagChange(event) {
      const parsedLag = timeParser.input(event.target.value, {
        durationData: this.instance.config.durationData,
        durationStep: this.instance.config.duration_view,
        prop: 'lag',
      });

      if (!parsedLag) {
        this.lag = this.parseInitialLag();

        return;
      }

      this.lag = parsedLag;
    },
    close() {
      app.trigger('closed:popup:settingsLink');
      app.trigger('popup:hide');
      this.onClose();
    },
    handleOverlayClick(event) {
      if (event.target === event.currentTarget) {
        this.close();
      }
    },
  },

};
</script>

<style lang="less" module>

.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.chart-link-popup {
  background: white;
  box-shadow: 0 9px 22px -2px rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  width: 438px;
}

.chart-link-popup__head {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.chart-link-popup__head-title {
  white-space: nowrap;
  overflow: hidden;
  display: inline-block;
  text-overflow: ellipsis;
  font-family: "Lato-Bold", sans-serif;
  color: #212121;
  text-transform: uppercase;
  font-size: 14px;
  line-height: 24px;
  padding-top: 2px;
}

.chart-link-popup__container {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  padding: 20px 30px;
}

.chart-link-popup__body {
  display: flex;
  position: relative;
  height: 84px;
  &::after {
    content: '';
    width: calc(100% + 60px);
    height: 1px;
    background: #cfcfcf;
    position: absolute;
    left: -30px;
    bottom: 0;
  }
}

.chart-link-popup__footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
}

.spin-input-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.spin-input {
  display: flex;
  width: 126px;
  height: 36px;
  padding: 0 6px;
  border: 1px solid #fbc02d;
  font-family: 'Lato-Regular', sans-serif;
  font-size: 14px
}

.spin-input__btn {
  font-size: 16px;
}

.spin-input__field {
  width: 100%;
  text-align: center;
  padding: 0 5px;
}

.spin-input-label {
  font-family: 'Lato-Regular', sans-serif;
  font-size: 14px;
}

</style>
